import React, { useMemo, useState, useEffect, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { Timestamp } from "firebase/firestore";
import { useGetUserByIdQuery } from "src/features/users/services/user.Service";
import { userRepository as UsersRepo } from "src/features/users/repositories/user.repository";
import { User } from "src/features/users/types/user.entity";
import { userFollowingRepository } from "src/features/users/repositories/user-following.repository";
import { useGetSlotsQuery, SlotsFilter } from "src/features/slots/slots.service";
import { Slot } from "src/features/slots/types/slot.entity";
import { useCreateBookingsByIdMutation } from "src/features/bookings/bookings.service";
import { bookingRepository } from "src/features/bookings/repositories/booking.repository";
import { showToast } from "src/utils/toast.util";
import Modal from "src/components/modal/modal";
import Swal from "sweetalert2";
import { FaCalendarAlt, FaUser, FaInfoCircle, FaClock, FaCheckCircle, FaExclamationCircle, FaChevronDown } from "react-icons/fa";

const groupByCoach = (slots: (Slot & { id: string })[]) => {
  const grouped = new Map<string, (Slot & { id: string })[]>();
  for (const s of slots) {
    const list = grouped.get(s.userId) || [];
    list.push(s);
    grouped.set(s.userId, list);
  }
  return grouped;
};

const formatDateWithNumberMonth = (date: any) => {
  const d = date instanceof Date ? date : (date as any).toDate?.() ?? new Date(date);
  return d.toLocaleDateString('en-GB', { 
    day: '2-digit',
    month: '2-digit', 
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const CreateBookingsForLearnerPage: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();

  const { data: learner } = useGetUserByIdQuery(userId || "");

  const [fromDate, setFromDate] = useState<string>("");
  const [toDate, setToDate] = useState<string>("");
  const [note, setNote] = useState<string>("");

  const [selectedCoachId, setSelectedCoachId] = useState<string | null>(null);
  const [selectedSlotIds, setSelectedSlotIds] = useState<string[]>([]);
  const [coachDetails, setCoachDetails] = useState<Map<string, { name: string; email: string }>>(new Map());
  const [bookedSlotIds, setBookedSlotIds] = useState<Set<string>>(new Set());
  const [showCoachDropdown, setShowCoachDropdown] = useState<boolean>(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const activeLang = learner?.appMeta?.activeLearningLangCode || undefined;

  const dateFilter = useMemo(() => {
    const f: SlotsFilter["date"] = {};
    if (fromDate) {
      const start = new Date(fromDate);
      start.setHours(0, 0, 0, 0);
      f.$gte = Timestamp.fromDate(start);
    }
    if (toDate) {
      const end = new Date(toDate);
      end.setHours(23, 59, 59, 999);
      f.$lte = Timestamp.fromDate(end);
    }
    return (f.$gte || f.$lte) ? f : undefined;
  }, [fromDate, toDate]);

  const { data: allSlots = [], isFetching, refetch } = useGetSlotsQuery(
    {
      where: {
        status: "AVAILABLE",
        ...(activeLang && { langCode: activeLang as any }),
        ...(dateFilter && { date: dateFilter as any }),
      },
      offset: undefined,
      limit: 100, // Increase limit to get more slots
    },
    { 
      skip: !activeLang && !dateFilter, // Only skip if no activeLang AND no date filter
      refetchOnFocus: true, 
      refetchOnReconnect: true 
    }
  );
  const handleRefresh = async () => {
    if (!activeLang && !dateFilter) {
      showToast("warrning", "No filters applied - please set a date range or ensure learner has an active learning language");
      return;
    }
    try {
      await refetch();
    } catch (e) {
      console.error("Failed to refresh slots:", e);
      showToast("error", "Failed to refresh slots");
    }
  };


  // Filter out slots that are not available for booking
  const slots = useMemo(() => {
    if (!userId) return allSlots;
    
    const filtered = allSlots.filter(slot => {
      // Remove slots that the current learner has already requested
      if (slot.requestedLearnersIds && slot.requestedLearnersIds.includes(userId)) {
        return false;
      }
      // Remove slots that are already booked by anyone (status BOOKED)
      if (slot.status === "BOOKED") {
        return false;
      }
      // Remove slots that this learner has already booked
      if (bookedSlotIds.has(slot.id)) {
        return false;
      }
      return true;
    });
    
    console.log(`Filtered slots: ${filtered.length} out of ${allSlots.length} total slots`);
    return filtered;
  }, [allSlots, userId, bookedSlotIds]);

  const grouped = useMemo(() => groupByCoach(slots), [slots]);
  
  // Get slots for the selected coach only
  const selectedCoachSlots = useMemo(() => {
    if (!selectedCoachId) return [];
    return grouped.get(selectedCoachId) || [];
  }, [grouped, selectedCoachId]);
  
  // Get all available coaches
  const availableCoaches = useMemo(() => {
    return Array.from(grouped.keys()).map(coachId => ({
      id: coachId,
      name: coachDetails.get(coachId)?.name || coachId,
      email: coachDetails.get(coachId)?.email || 'Loading...',
      slotCount: grouped.get(coachId)?.length || 0
    }));
  }, [grouped, coachDetails]);

  // Fetch booked slots for this learner
  useEffect(() => {
    const fetchBookedSlots = async () => {
      if (!userId) return;
      
      try {
        const bookings = await bookingRepository.find({
          where: {
            learnerId: userId,
            bookingStatus: { $in: ["INIT", "ACCEPTED"] },
          } as any,
        });
        
        const bookedSlotIdsSet = new Set(bookings.map((booking: any) => booking.slotId));
        setBookedSlotIds(bookedSlotIdsSet);
      } catch (error) {
        console.error("Error fetching booked slots:", error);
      }
    };

    fetchBookedSlots();
  }, [userId]);

  // Auto-approval is computed lazily on proceed for the selected coach only

  const [slotDetails, setSlotDetails] = useState<null | (Slot & { id: string })>(null);

  // Fetch coach details when slots are loaded (batched & memoized)
  const loadedCoachIdsRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    const fetchCoachDetails = async () => {
      const coachIds = Array.from(grouped.keys());
      const missingCoachIds = coachIds.filter((id) => !loadedCoachIdsRef.current.has(id));
      if (missingCoachIds.length === 0) return;

      try {
        const coaches = (await UsersRepo.findByIds(missingCoachIds)) as (User & { id: string })[];
        const details = new Map<string, { name: string; email: string }>();
        for (const coach of coaches) {
          details.set(coach.id, {
            name: coach.name || coach.id,
            email: coach.email || "No email available",
          });
        }
        for (const id of missingCoachIds) loadedCoachIdsRef.current.add(id);
        setCoachDetails((prev) => {
          const merged = new Map(prev);
          details.forEach((value, key) => {
            merged.set(key, value);
          });
          return merged;
        });
      } catch (error) {
        console.error("Error fetching coach details:", error);
      }
    };

    if (grouped.size > 0) {
      fetchCoachDetails();
    }
  }, [grouped]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowCoachDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const toggleSlot = (slot: Slot & { id: string }) => {
    if (bookedSlotIds.has(slot.id)) {
      showToast("warrning", "This slot is already booked");
      return;
    }
    const exists = selectedSlotIds.includes(slot.id);
    if (exists) {
      setSelectedSlotIds((prev) => prev.filter((id) => id !== slot.id));
    } else {
      setSelectedSlotIds((prev) => [...prev, slot.id]);
    }
  };
  
  const handleCoachSelect = (coachId: string) => {
    setSelectedCoachId(coachId);
    setSelectedSlotIds([]); // Clear selected slots when switching coaches
    setShowCoachDropdown(false);
  };

  const [createBookings, { isLoading: creating }] = useCreateBookingsByIdMutation();

  const handleProceed = async () => {
    if (!userId || selectedSlotIds.length === 0) {
      showToast("warrning", "Please select at least one slot to create a booking");
      return;
    }
    if (!selectedCoachId) {
      showToast("warrning", "Please select slots from a coach before proceeding");
      return;
    }

    // Fetch coach and check auto-approval
    let isAutoApproved = false;
    try {
      const coach = (await UsersRepo.findById(selectedCoachId)) as (User & { id: string }) | null;
      if (coach) {
        const approval = coach.appSettings?.coachSettings?.bookingApproval;
        if (approval === "ALL_LEARNERS") {
          isAutoApproved = true;
        } else if (approval === "LEARNERS_I_FOLLOW") {
          isAutoApproved = await userFollowingRepository.isCoachFollowingLearner(selectedCoachId, userId);
        }
      }
    } catch (error) {
      console.error("Error checking auto-approval:", error);
      showToast("warrning", "Unable to verify coach settings. Proceeding with manual approval required.");
    }

    // Show confirmation with SweetAlert2 (keeping last design)
    const coachName = coachDetails.get(selectedCoachId)?.name || selectedCoachId;
    
    const result = await Swal.fire({
      title: 'Confirm Booking Creation',
      html: `
        <div class="text-left">
          <p class="mb-3">You are about to create <strong>${selectedSlotIds.length}</strong> booking(s) with coach <strong>${coachName}</strong>.</p>
          <div class="flex items-center gap-2 mb-3">
            <div class="w-6 h-6 rounded-full flex items-center justify-center ${isAutoApproved ? 'bg-green-100' : 'bg-yellow-100'}">
              ${isAutoApproved ? '<svg class="w-4 h-4 text-green-600" fill="currentColor" viewBox="0 0 20 20"><path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"></path></svg>' : '<svg class="w-4 h-4 text-yellow-600" fill="currentColor" viewBox="0 0 20 20"><path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.726-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd"></path></svg>'}
            </div>
            <span class="text-sm ${isAutoApproved ? 'text-green-700' : 'text-yellow-700'}">
              ${isAutoApproved ? 'Auto-approved' : 'Requires coach approval'}
            </span>
          </div>
          ${note ? `<p class="text-sm text-gray-600">Note: ${note}</p>` : ''}
        </div>
      `,
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#10b981',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Create Bookings',
      cancelButtonText: 'Cancel',
      width: '500px'
    });

    if (!result.isConfirmed) return;
    
    try {
      // Show loading while creating bookings
      Swal.fire({
        title: 'Creating bookings... ',
        html: 'Please wait while we process your request.',
        allowOutsideClick: false,
        width: '400px',
        didOpen: () => {
          Swal.showLoading();
        },
      });

      const res: any = await createBookings({ userId: userId!, slotsIds: selectedSlotIds, note });
      
      // Check if the response contains an error
      if ("error" in res) {
        // Close loading before showing error
        Swal.close();
        console.error("Booking creation error - Full response:", res);
        console.error("Booking creation error - Error object:", res.error);
        console.error("Booking creation error - Error data:", res.error?.data);
        
        // Extract error message from different possible structures
        let errorMessage: string = "Unknown error occurred";
        let errorCode: string = "";
        
        // Try different error extraction methods
        if (res.error?.data) {
          console.log("Error data type:", typeof res.error.data);
          console.log("Error data content:", res.error.data);
          
          if (typeof res.error.data === 'string') {
            errorMessage = res.error.data;
          } else if (res.error.data?.message) {
            errorMessage = String(res.error.data.message);
            errorCode = String(res.error.data.code || "");
          } else if (res.error.data?.error) {
            errorMessage = String(res.error.data.error);
            errorCode = String(res.error.data.code || "");
          } else if (res.error.data?.details) {
            errorMessage = String(res.error.data.details);
            errorCode = String(res.error.data.code || "");
          } else if (typeof res.error.data === 'object') {
            // Try to find any string property that might contain the error
            const possibleMessages = Object.values(res.error.data).filter(val => 
              typeof val === 'string' && val.length > 0
            );
            if (possibleMessages.length > 0) {
              errorMessage = String(possibleMessages[0]);
            }
          }
        } else if (res.error?.message) {
          errorMessage = String(res.error.message);
          errorCode = String(res.error.code || "");
        } else if (res.error?.details) {
          errorMessage = String(res.error.details);
          errorCode = String(res.error.code || "");
        }
        
        console.log("Extracted error message:", errorMessage);
        console.log("Extracted error code:", errorCode);
        
        // If we still have the default message, try to show raw error data
        if (errorMessage === "Unknown error occurred") {
          console.log("Could not extract error message, showing raw error data");
          errorMessage = JSON.stringify(res.error, null, 2);
        }
        
        // Display the actual error from the callable function in a popup
        let displayMessage = errorMessage;
        
        // Add error code if available
        if (errorCode) {
          displayMessage = `[${errorCode}] ${errorMessage}`;
        }
        
        // Show specific error messages for common cases
        if (errorMessage.includes('slot already booked') || errorMessage.includes('already booked')) {
          displayMessage = `❌ Slot Already Booked: ${errorMessage}`;
        } else if (errorMessage.includes('not found') || errorMessage.includes('does not exist')) {
          displayMessage = `❌ Not Found: ${errorMessage}`;
        } else if (errorMessage.includes('balance') || errorMessage.includes('insufficient')) {
          displayMessage = `❌ Insufficient Balance: ${errorMessage}`;
        } else if (errorMessage.includes('same coach') || errorMessage.includes('different coach')) {
          displayMessage = `❌ Coach Mismatch: ${errorMessage}`;
        } else if (errorMessage.includes('expired') || errorMessage.includes('invalid date')) {
          displayMessage = `❌ Expired Slots: ${errorMessage}`;
        } else if (errorMessage.includes('unauthenticated') || errorMessage.includes('authentication')) {
          displayMessage = `❌ Authentication Error: ${errorMessage}`;
        } else if (errorMessage.includes('permission') || errorMessage.includes('unauthorized')) {
          displayMessage = `❌ Permission Denied: ${errorMessage}`;
        } else if (errorMessage.includes('validation') || errorMessage.includes('invalid')) {
          displayMessage = `❌ Validation Error: ${errorMessage}`;
        } else {
          displayMessage = `❌ Booking Error: ${errorMessage}`;
        }
        
        await Swal.fire({
          title: 'Booking Failed',
          text: displayMessage,
          icon: 'error',
          showConfirmButton: false,
          timer: 1500,
          timerProgressBar: true,
          width: '400px',
          allowOutsideClick: false,
          allowEscapeKey: false,
          customClass: {
            popup: 'rounded-lg',
            title: 'text-xl font-bold text-red-600',
            htmlContainer: 'text-gray-700'
          }
        });
        return;
      }
      
      // Success case - close loading and show success
      Swal.close();
      await Swal.fire({
        title: 'Booking Created!',
        text: `Successfully created ${selectedSlotIds.length} booking${selectedSlotIds.length !== 1 ? 's' : ''}! ${isAutoApproved ? 'Auto-approved' : 'Pending approval'}`,
        icon: 'success',
        showConfirmButton: false,
        timer: 1000,
        timerProgressBar: true,
        width: '400px',
        allowOutsideClick: false,
        allowEscapeKey: false,
        customClass: {
          popup: 'rounded-lg',
          title: 'text-xl font-bold text-green-600',
          htmlContainer: 'text-gray-700'
        }
      });
      
      // Clear selected slots to allow more operations, preserve selected coach
      setSelectedSlotIds([]);
       
       // Update booked slots to include the newly created bookings
       setBookedSlotIds(prev => {
         const newSet = new Set(prev);
         selectedSlotIds.forEach(slotId => newSet.add(slotId));
         return newSet;
       });
       
       // Refresh slots to reflect the new bookings
       try {
         await refetch();
       } catch (e) {
         console.error("Failed to refresh slots after booking:", e);
       }
    } catch (error) {
      // Ensure any loading modal is closed
      Swal.close();
      console.error("Unexpected error creating bookings:", error);
      const errorMsg = error instanceof Error ? error.message : "Unknown error occurred";
      
      await Swal.fire({
        title: 'Unexpected Error',
        text: `An unexpected error occurred: ${errorMsg}`,
        icon: 'error',
        showConfirmButton: false,
        timer: 1500,
        timerProgressBar: true,
        width: '400px',
        allowOutsideClick: false,
        allowEscapeKey: false,
        customClass: {
          popup: 'rounded-lg',
          title: 'text-xl font-bold text-red-600',
          htmlContainer: 'text-gray-700'
        }
      });
    }
  };

  return (
    <>
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl flex items-center justify-center shadow-lg">
                <FaCalendarAlt className="text-white text-2xl" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-gray-900">Create Bookings</h1>
                <p className="text-gray-600">Schedule sessions for learner: <span className="font-semibold text-emerald-600">{learner?.name || userId}</span></p>
              </div>
            </div>
                <Link
                  to={`/users/${userId}`}
                  className="px-6 py-3 bg-black hover:bg-gray-800 text-white rounded-lg shadow-sm hover:shadow-md transition-all duration-200 flex items-center gap-2 border border-black hover:border-gray-800 no-underline"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                  </svg>
                  <span className="font-medium">Back to User</span>
                </Link>
          </div>

          {/* Filters */}
          <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <FaClock className="text-emerald-600" />
              Filter Slots
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              <div className="group">
                <label className="block text-sm font-semibold text-gray-800 mb-3 flex items-center gap-2">
                  <div className="w-2 h-2 bg-gradient-to-r from-emerald-500 to-teal-500 rounded-full"></div>
                  From Date
                </label>
                <input 
                  type="date" 
                  value={fromDate} 
                  onChange={(e) => setFromDate(e.target.value)} 
                  className="w-full border-2 border-gray-200 rounded-2xl px-6 py-4 text-gray-700 bg-white/80 backdrop-blur-sm focus:ring-4 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all duration-300 hover:border-gray-300 hover:bg-white shadow-sm hover:shadow-md group-hover:shadow-lg" 
                />
              </div>
              <div className="group">
                <label className="block text-sm font-semibold text-gray-800 mb-3 flex items-center gap-2">
                  <div className="w-2 h-2 bg-gradient-to-r from-emerald-500 to-teal-500 rounded-full"></div>
                  To Date
                </label>
                <input 
                  type="date" 
                  value={toDate} 
                  onChange={(e) => setToDate(e.target.value)} 
                  className="w-full border-2 border-gray-200 rounded-2xl px-6 py-4 text-gray-700 bg-white/80 backdrop-blur-sm focus:ring-4 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all duration-300 hover:border-gray-300 hover:bg-white shadow-sm hover:shadow-md group-hover:shadow-lg" 
                />
              </div>
                  <div className="flex items-end">
                    <button
                      onClick={() => {
                        setFromDate("");
                        setToDate("");
                      }}
                      className="w-12 h-12 bg-black hover:bg-gray-800 text-white rounded-xl border border-black hover:border-gray-800 transition-all duration-200 flex items-center justify-center shadow-lg hover:shadow-xl transform hover:scale-105 group"
                      title="Reset Filters"
                    >
                      <svg className="w-5 h-5 group-hover:rotate-180 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                      </svg>
                    </button>
                  </div>
            </div>
          </div>
        </div>

        {/* Warning for missing language */}
        {!activeLang && (
          <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-xl flex items-center gap-3">
            <FaExclamationCircle className="text-amber-600 text-xl" />
            <div>
              <p className="font-medium text-amber-800">No Active Learning Language</p>
              <p className="text-sm text-amber-700">Learner has no active learning language set. No slots will be available.</p>
            </div>
          </div>
        )}

         {/* Coach Selection */}
         <div className="mb-8">
           <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100">
             <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
               <FaUser className="text-emerald-600" />
               Select Coach
             </h3>
             
             {availableCoaches.length === 0 ? (
               <div className="text-center py-8">
                 <p className="text-gray-600">No coaches available with slots matching your filters.</p>
               </div>
             ) : (
               <div className="relative" ref={dropdownRef}>
                 <button
                   onClick={() => setShowCoachDropdown(!showCoachDropdown)}
                   className="w-full px-4 py-3 text-left border border-gray-300 rounded-xl bg-white hover:border-emerald-500 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-colors flex items-center justify-between"
                 >
                   <div className="flex items-center gap-3">
                     <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl flex items-center justify-center">
                       <FaUser className="text-white text-sm" />
                     </div>
                     <div>
                       {selectedCoachId ? (
                         <div>
                           <div className="font-medium text-gray-900">
                             {coachDetails.get(selectedCoachId)?.name || selectedCoachId}
                           </div>
                           <div className="text-sm text-gray-500">
                             {coachDetails.get(selectedCoachId)?.email || 'Loading...'}
                           </div>
                         </div>
                       ) : (
                         <span className="text-gray-500">Choose a coach...</span>
                       )}
                     </div>
                   </div>
                   <FaChevronDown className={`text-gray-400 transition-transform ${showCoachDropdown ? 'rotate-180' : ''}`} />
                 </button>
                 
                 {showCoachDropdown && (
                   <div className="absolute top-full left-0 right-0 mt-2 bg-white border border-gray-200 rounded-xl shadow-lg z-50 max-h-80 overflow-y-auto">
                     {availableCoaches.map((coach) => (
                       <button
                         key={coach.id}
                         onClick={() => handleCoachSelect(coach.id)}
                         className="w-full px-4 py-3 text-left hover:bg-gray-50 border-b border-gray-100 last:border-b-0 flex items-center gap-3"
                       >
                         <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl flex items-center justify-center">
                           <FaUser className="text-white text-sm" />
                         </div>
                         <div className="flex-1">
                           <div className="font-medium text-gray-900">{coach.name}</div>
                           <div className="text-sm text-gray-500">{coach.email}</div>
                           <div className="text-xs text-emerald-600 font-medium">
                             {coach.slotCount} available slot{coach.slotCount !== 1 ? 's' : ''}
                           </div>
                         </div>
                         <Link 
                           to={`/users/${coach.id}`}
                           className="inline-flex items-center px-2 py-1 rounded-full bg-gradient-to-r from-cyan-500 via-sky-500 to-indigo-500 text-white border border-sky-300 shadow-sm hover:shadow-md hover:from-cyan-600 hover:via-sky-600 hover:to-indigo-600 transition-all duration-200 transform hover:scale-110 will-change-transform no-underline font-mono font-semibold text-[10px] sm:text-[11px] focus:outline-none focus:ring-2 focus:ring-sky-300 focus:ring-offset-1 select-text"
                           onClick={(e) => e.stopPropagation()}
                         >
                           {coach.id}
                         </Link>
                       </button>
                     ))}
                   </div>
                 )}
               </div>
             )}
           </div>
         </div>

         {/* Slots Section */}
         <div className="mb-8">
           <div className="flex items-center justify-between mb-6">
             <h3 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
               {selectedCoachId ? (
                 <>
                   <FaCalendarAlt className="text-emerald-600" />
                   Available Slots - {coachDetails.get(selectedCoachId)?.name || selectedCoachId}
                 </>
               ) : (
                 <>
                   <FaCalendarAlt className="text-emerald-600" />
                   Select a coach to view available slots
                 </>
               )}
             </h3>
             <button
               onClick={handleRefresh}
               disabled={isFetching || (!activeLang && !dateFilter)}
               className={`px-4 py-2 rounded-lg shadow-lg hover:shadow-xl transition-all duration-200 flex items-center gap-2 border ${
                 isFetching || (!activeLang && !dateFilter)
                   ? "bg-gray-400 border-gray-400 text-gray-200 cursor-not-allowed"
                   : "bg-black hover:bg-gray-800 text-white border-black hover:border-gray-800"
               }`}
               title="Refresh Slots"
             >
               <svg 
                 className={`w-4 h-4 ${isFetching ? 'animate-spin' : ''}`} 
                 fill="none" 
                 stroke="currentColor" 
                 viewBox="0 0 24 24"
               >
                 <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
               </svg>
               <span className="font-medium">{isFetching ? 'Refreshing...' : 'Refresh'}</span>
             </button>
           </div>

           {isFetching ? (
             <div className="bg-white rounded-2xl shadow-lg p-12 text-center">
               <div className="animate-spin w-12 h-12 border-4 border-emerald-500 border-t-transparent rounded-full mx-auto mb-4"></div>
               <p className="text-gray-600">Loading available slots...</p>
             </div>
           ) : !selectedCoachId ? (
             <div className="bg-white rounded-2xl shadow-lg p-12 text-center border border-gray-100">
               <div className="w-16 h-16 bg-gradient-to-br from-gray-100 to-gray-200 rounded-2xl flex items-center justify-center mx-auto mb-4">
                 <FaUser className="text-gray-400 text-2xl" />
               </div>
               <p className="text-gray-600">Please select a coach to view their available slots.</p>
             </div>
           ) : selectedCoachSlots.length === 0 ? (
             <div className="bg-white rounded-2xl shadow-lg p-12 text-center border border-gray-100">
               <p className="text-gray-600">No available slots found for the selected coach and filters.</p>
             </div>
           ) : (
             <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
               <div className="px-6 py-4 border-b border-gray-100 bg-gradient-to-r from-gray-50 to-gray-100">
                 <div className="flex items-center justify-between">
                   <div className="flex items-center gap-3">
                     <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl flex items-center justify-center">
                       <FaUser className="text-white text-sm" />
                     </div>
                     <div>
                       <h4 className="font-semibold text-gray-900">Coach</h4>
                       <div className="text-sm text-gray-600">
                         <div className="font-medium">{coachDetails.get(selectedCoachId)?.name || selectedCoachId}</div>
                         <div className="text-xs text-gray-500">{coachDetails.get(selectedCoachId)?.email || 'Loading...'}</div>
                         <Link 
                           to={`/users/${selectedCoachId}`}
                           className="inline-flex items-center px-2.5 py-1 rounded-full bg-gradient-to-r from-cyan-500 via-sky-500 to-indigo-500 text-white border border-sky-300 shadow-sm hover:shadow-md hover:from-cyan-600 hover:via-sky-600 hover:to-indigo-600 transition-all duration-200 transform hover:scale-110 will-change-transform no-underline font-mono font-semibold text-[10px] sm:text-[11px] focus:outline-none focus:ring-2 focus:ring-sky-300 focus:ring-offset-1 select-text mt-1"
                         >
                           {selectedCoachId}
                         </Link>
                       </div>
                     </div>
                   </div>
                   {selectedSlotIds.length > 0 && (
                     <div className="flex items-center gap-2 px-3 py-1 bg-emerald-100 text-emerald-700 rounded-full text-sm font-medium">
                       <FaCheckCircle className="text-sm" />
                       {selectedSlotIds.length} selected
                     </div>
                   )}
                 </div>
               </div>
               <div className="p-6">
                 <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
                   {selectedCoachSlots.map((slot) => {
                     const isPro = slot.coachType === 'PRO';
                     return (
                       <div key={slot.id} className="relative group">
                         <button
                           onClick={() => toggleSlot(slot)}
                           disabled={bookedSlotIds.has(slot.id)}
                           aria-disabled={bookedSlotIds.has(slot.id)}
                           className={`w-full px-4 py-3 text-sm rounded-xl border-2 transition-all duration-200 ${
                             selectedSlotIds.includes(slot.id)
                               ? "bg-black border-black text-yellow-400 shadow-md"
                               : bookedSlotIds.has(slot.id)
                                 ? "bg-gradient-to-br from-orange-50 to-red-50 border-orange-200 hover:from-orange-100 hover:to-red-100 text-orange-800 hover:text-orange-900"
                                 : isPro
                                   ? "bg-gradient-to-br from-yellow-50 to-amber-50 border-yellow-200 hover:from-yellow-100 hover:to-amber-100 text-yellow-800 hover:text-yellow-900"
                                   : "bg-gradient-to-br from-teal-50 to-cyan-50 border-teal-200 hover:from-teal-100 hover:to-cyan-100 text-teal-800 hover:text-teal-900"
                           } ${bookedSlotIds.has(slot.id) ? 'cursor-not-allowed' : ''}`}
                         >
                           <div className="text-center">
                             <div className="font-medium">{formatDateWithNumberMonth(slot.date)}</div>
                             <div className={`text-xs mt-1 px-2 py-1 rounded-full ${
                               bookedSlotIds.has(slot.id)
                                 ? 'bg-orange-100 text-orange-700'
                                 : isPro 
                                   ? 'bg-yellow-100 text-yellow-700' 
                                   : 'bg-teal-100 text-teal-700'
                             }`}>
                               {bookedSlotIds.has(slot.id) ? 'BOOKED' : slot.coachType}
                             </div>
                           </div>
                         </button>
                         <button
                           onClick={() => setSlotDetails(slot)}
                           className="absolute -top-2 -right-2 w-6 h-6 bg-blue-500 text-white rounded-full flex items-center justify-center text-xs hover:bg-blue-600 transition-colors shadow-md"
                           title="View slot details"
                         >
                           <FaInfoCircle className="text-xs" />
                         </button>
                       </div>
                     );
                   })}
                 </div>
               </div>
             </div>
           )}
         </div>

        {/* Action Section */}
        <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100">
          <div className="flex items-center justify-between gap-6">
            <div className="flex items-center gap-4 flex-1">
              <div className="flex-1 max-w-md">
                <input 
                  type="text" 
                  value={note} 
                  onChange={(e) => setNote(e.target.value)} 
                  placeholder="Add a note for the coach (optional)" 
                  className="w-full border border-gray-300 rounded-xl px-4 py-3 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-colors" 
                />
              </div>
              <button
                onClick={handleProceed}
                disabled={creating || selectedSlotIds.length === 0}
                className={`px-8 py-4 rounded-xl font-semibold text-white transition-all duration-200 flex items-center gap-3 ${
                  creating || selectedSlotIds.length === 0
                    ? "bg-gray-400 cursor-not-allowed"
                    : "bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 shadow-lg hover:shadow-xl transform hover:scale-105"
                }`}
              >
                {creating ? (
                  <>
                    <div className="animate-spin w-5 h-5 border-2 border-white border-t-transparent rounded-full"></div>
                    Processing...
                  </>
                ) : (
                  <>
                    <FaCheckCircle className="text-lg" />
                    Create {selectedSlotIds.length > 0 ? `${selectedSlotIds.length} ` : ''}Booking{selectedSlotIds.length !== 1 ? 's' : ''}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
    <Modal isOpen={!!slotDetails} onClose={() => setSlotDetails(null)} size={2}>
      {slotDetails && (
        <div className="space-y-6">
          <div className="text-center">
            <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <FaCalendarAlt className="text-white text-2xl" />
            </div>
            <h3 className="text-2xl font-bold text-gray-900">Slot Details</h3>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-gray-50 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-2">
                <FaInfoCircle className="text-blue-500" />
                <span className="font-semibold text-gray-700">Slot ID</span>
              </div>
              <Link 
                to={`/slots/${slotDetails.id}`}
                className="inline-flex items-center px-2.5 py-1 rounded-full bg-gradient-to-r from-cyan-500 via-sky-500 to-indigo-500 text-white border border-sky-300 shadow-sm hover:shadow-md hover:from-cyan-600 hover:via-sky-600 hover:to-indigo-600 transition-all duration-200 transform hover:scale-110 will-change-transform no-underline font-mono font-semibold text-[10px] sm:text-[11px] focus:outline-none focus:ring-2 focus:ring-sky-300 focus:ring-offset-1 select-text"
              >
                {slotDetails.id}
              </Link>
            </div>
            
            <div className="bg-gray-50 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-2">
                <FaUser className="text-blue-500" />
                <span className="font-semibold text-gray-700">Coach ID</span>
              </div>
              <Link 
                to={`/users/${slotDetails.userId}`}
                className="inline-flex items-center px-2.5 py-1 rounded-full bg-gradient-to-r from-cyan-500 via-sky-500 to-indigo-500 text-white border border-sky-300 shadow-sm hover:shadow-md hover:from-cyan-600 hover:via-sky-600 hover:to-indigo-600 transition-all duration-200 transform hover:scale-110 will-change-transform no-underline font-mono font-semibold text-[10px] sm:text-[11px] focus:outline-none focus:ring-2 focus:ring-sky-300 focus:ring-offset-1 select-text"
              >
                {slotDetails.userId}
              </Link>
            </div>
            
            <div className="bg-gray-50 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-2">
                <FaClock className="text-green-500" />
                <span className="font-semibold text-gray-700">Date & Time</span>
              </div>
              <p className="text-sm text-gray-600">{formatDateWithNumberMonth(slotDetails.date)}</p>
            </div>
            
            <div className="bg-gray-50 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-2">
                <FaCheckCircle className="text-emerald-500" />
                <span className="font-semibold text-gray-700">Status</span>
              </div>
              <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold ${
                slotDetails.status === 'AVAILABLE' 
                  ? 'bg-green-100 text-green-700' 
                  : slotDetails.status === 'BOOKED'
                  ? 'bg-red-100 text-red-700'
                  : slotDetails.status === 'EXPIRED'
                  ? 'bg-gray-100 text-gray-700'
                  : 'bg-yellow-100 text-yellow-700'
              }`}>
                {slotDetails.status}
              </span>
            </div>
            
            <div className="bg-gray-50 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-2">
                <span className="font-semibold text-gray-700">Language</span>
              </div>
              <p className="text-sm text-gray-600">{String(slotDetails.langCode)}</p>
            </div>

            <div className="bg-gray-50 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-2">
                <span className="font-semibold text-gray-700">Coach Type</span>
              </div>
              <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold ${
                slotDetails.coachType === 'PRO' 
                  ? 'bg-yellow-100 text-yellow-700' 
                  : 'bg-teal-100 text-teal-700'
              }`}>
                {slotDetails.coachType}
              </span>
            </div>

            {slotDetails.groupId && (
              <div className="bg-gray-50 rounded-xl p-4">
                <div className="flex items-center gap-2 mb-2">
                  <span className="font-semibold text-gray-700">Group ID</span>
                </div>
                <p className="text-sm text-gray-600 font-mono">{slotDetails.groupId}</p>
              </div>
            )}

            <div className="bg-gray-50 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-2">
                <span className="font-semibold text-gray-700">Created</span>
              </div>
              <p className="text-sm text-gray-600">{formatDateWithNumberMonth(slotDetails.createdAt)}</p>
            </div>
            
          </div>

          <div className="bg-gray-50 rounded-xl p-4">
            <div className="flex items-center gap-2 mb-3">
              <span className="font-semibold text-gray-700">Requested Learners</span>
              <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">
                {slotDetails.requestedLearnersIds?.length || 0}
              </span>
              <span className="text-xs text-gray-500">(Multiple learners can request the same slot)</span>
            </div>
            {slotDetails.requestedLearnersIds && slotDetails.requestedLearnersIds.length > 0 ? (
              <div className="space-y-2">
                {slotDetails.requestedLearnersIds.map((learnerId, index) => (
                  <Link 
                    key={index}
                    to={`/users/${learnerId}`}
                    className="inline-flex items-center px-2.5 py-1 rounded-full bg-gradient-to-r from-cyan-500 via-sky-500 to-indigo-500 text-white border border-sky-300 shadow-sm hover:shadow-md hover:from-cyan-600 hover:via-sky-600 hover:to-indigo-600 transition-all duration-200 transform hover:scale-110 will-change-transform no-underline font-mono font-semibold text-[10px] sm:text-[11px] focus:outline-none focus:ring-2 focus:ring-sky-300 focus:ring-offset-1 select-text mr-2 mb-2"
                  >
                    {learnerId}
                  </Link>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-500 italic">No learners have requested this slot yet</p>
            )}
          </div>
        </div>
      )}
    </Modal>

    </>
  );
};

export default CreateBookingsForLearnerPage;


